import React from 'react';
import { ActivePageHeading } from './ActivePageHeading';
import { ModernButton } from '../ui/ModernButton';
import { TrashIcon, SaveIcon } from '../ui/Icons';
import { QuestionEditor } from './QuestionEditor';
import { computeDisplayLabels } from '../../utils/questionnairePages';
import { isConditionSource, getPageTypeLabel } from '../../utils/questionnaireEditorUtils';

export function CustomPageContent({
  activePage,
  activePageQuestions,
  activePageType,
  isBasisPageSelected,
  isFachPageSelected,
  selectedBasicTemplateId,
  setSelectedBasicTemplateId,
  loadBasicQuestionTemplate,
  basicQuestionTemplates,
  defaultBasisTemplateId,
  deleteBasicQuestionTemplateById,
  basicTemplateName,
  setBasicTemplateName,
  saveBasicQuestionTemplate,
  setBasisTemplateAsDefault,
  resetBasisTemplateDefault,
  configuredBasisTemplateName,
  selectedFachTemplateId,
  setSelectedFachTemplateId,
  loadFachQuestionTemplate,
  fachQuestionTemplates,
  defaultFachTemplateId,
  deleteFachQuestionTemplateById,
  fachTemplateName,
  setFachTemplateName,
  saveFachQuestionTemplate,
  setFachTemplateAsDefault,
  resetFachTemplateDefault,
  configuredFachTemplateName,
  draggedIndex,
  dragOverIndex,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragStart,
  handleDragEnd,
  setDraggedIndex,
  setDragOverIndex,
  handleOptionDragStart,
  handleOptionDragOver,
  handleOptionDragEnd,
  draggedOptionIndex,
  dragOverOptionIndex,
  setQuestionConditional,
  updateQuestion,
  removeQuestion,
  handleOptionChange,
  handleOptionScoreChange,
  addOption,
  removeOption,
  questionPages,
  moveQuestionToPage,
  pageNumber,
}) {
  return (
    <div>
      <ActivePageHeading
                number={pageNumber}
        title={activePage?.title || 'Keine Seite ausgewählt'}
        meta={`${activePageQuestions.length} Frage${activePageQuestions.length === 1 ? '' : 'n'} · ${getPageTypeLabel(activePageType)}`}
      />
      {isBasisPageSelected && (
        <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="flex-1 max-w-2xl">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Basisfragen-Vorlage laden</label>
              <div className="flex items-stretch gap-3">
                <select
                  value={selectedBasicTemplateId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedBasicTemplateId(id);
                    if (id) loadBasicQuestionTemplate(id);
                  }}
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-sm"
                >
                  <option value="">Vorlage für Basisfragen auswählen...</option>
                  {basicQuestionTemplates.map((template) => {
                    const isDefaultTemplate = String(template.id) === String(defaultBasisTemplateId);
                    return (
                      <option key={template.id} value={template.id}>
                        {isDefaultTemplate ? '★ ' : ''}{template.name}{isDefaultTemplate ? ' (Standard)' : ''}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={() => deleteBasicQuestionTemplateById(selectedBasicTemplateId)}
                  disabled={!selectedBasicTemplateId}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center min-w-[44px]"
                  aria-label="Vorlage für Basisfragen löschen"
                  title="Vorlage für Basisfragen löschen"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
            <div className="flex w-full sm:w-auto min-w-0 max-w-xs">
              <input
                type="text"
                value={basicTemplateName}
                onChange={(e) => setBasicTemplateName(e.target.value)}
                placeholder="Vorlagenname"
                className="flex-1 p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-l-lg min-w-0 focus:outline-none focus:ring-0 focus:border-green-600 text-sm"
              />
              <button
                onClick={saveBasicQuestionTemplate}
                className="px-3 py-2 bg-slate-600 text-white rounded-r-lg hover:bg-slate-700 flex items-center justify-center whitespace-nowrap text-sm"
                aria-label="Vorlage für Basisfragen speichern"
                title="Vorlage für Basisfragen speichern"
              >
                <SaveIcon />
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <ModernButton onClick={setBasisTemplateAsDefault} variant="secondary" size="sm">
              Als Standard setzen
            </ModernButton>
            <ModernButton onClick={resetBasisTemplateDefault} variant="secondary" size="sm">
              Standard zurücksetzen
            </ModernButton>
            {configuredBasisTemplateName && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                Aktuell: {configuredBasisTemplateName}
              </span>
            )}
          </div>
        </div>
      )}
      {isFachPageSelected && (
        <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="flex-1 max-w-2xl">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Fachspezifische Vorlage laden</label>
              <div className="flex items-stretch gap-3">
                <select
                  value={selectedFachTemplateId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedFachTemplateId(id);
                    if (id) loadFachQuestionTemplate(id);
                  }}
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-sm"
                >
                  <option value="">Vorlage für fachspezifische Fragen auswählen...</option>
                  {fachQuestionTemplates.map((template) => {
                    const isDefaultTemplate = String(template.id) === String(defaultFachTemplateId);
                    return (
                      <option key={template.id} value={template.id}>
                        {isDefaultTemplate ? '★ ' : ''}{template.name}{isDefaultTemplate ? ' (Standard)' : ''}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={() => deleteFachQuestionTemplateById(selectedFachTemplateId)}
                  disabled={!selectedFachTemplateId}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center min-w-[44px]"
                  aria-label="Fachspezifische Vorlage löschen"
                  title="Fachspezifische Vorlage löschen"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
            <div className="flex w-full sm:w-auto min-w-0 max-w-xs">
              <input
                type="text"
                value={fachTemplateName}
                onChange={(e) => setFachTemplateName(e.target.value)}
                placeholder="Vorlagenname"
                className="flex-1 p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-l-lg min-w-0 focus:outline-none focus:ring-0 focus:border-green-600 text-sm"
              />
              <button
                onClick={saveFachQuestionTemplate}
                className="px-3 py-2 bg-slate-600 text-white rounded-r-lg hover:bg-slate-700 flex items-center justify-center whitespace-nowrap text-sm"
                aria-label="Fachspezifische Vorlage speichern"
                title="Fachspezifische Vorlage speichern"
              >
                <SaveIcon />
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <ModernButton onClick={setFachTemplateAsDefault} variant="secondary" size="sm">
              Als Standard setzen
            </ModernButton>
            <ModernButton onClick={resetFachTemplateDefault} variant="secondary" size="sm">
              Standard zurücksetzen
            </ModernButton>
            {configuredFachTemplateName && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                Aktuell: {configuredFachTemplateName}
              </span>
            )}
          </div>
        </div>
      )}
      <div className="space-y-6">{activePageQuestions.length > 0 ? (() => { const activeLabelMap = computeDisplayLabels(activePageQuestions); return activePageQuestions.map((q, index) => (
        <div
          key={q.id}
          data-question-id={q.id}
          onDragOver={(e) => {
            e.preventDefault();
            if (draggedIndex !== null) {
              handleDragOver(e, index);
            }
          }}
          onDragLeave={(e) => {
            if (draggedIndex !== null) {
              handleDragLeave(e);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedIndex !== null) {
              handleDrop(e, index);
            }
          }}
          className={`transition-all duration-200 ${draggedIndex === index ? 'opacity-50 scale-95' : ''
            } ${dragOverIndex === index && draggedIndex !== index ? 'border-2 border-dashed border-green-400 bg-green-50 dark:bg-green-900/20' : ''
            }`}
        >
          <QuestionEditor
            question={q}
            index={index}
            displayLabel={activeLabelMap[q.id]}
            labelMap={activeLabelMap}
            conditionalCandidates={activePageQuestions
              .filter((candidate) => isConditionSource(candidate) && String(candidate.id) !== String(q.id))}
            onConditionalChange={setQuestionConditional}
            updateQuestion={updateQuestion}
            removeQuestion={removeQuestion}
            handleOptionChange={handleOptionChange}
            handleOptionScoreChange={handleOptionScoreChange}
            addOption={addOption}
            removeOption={removeOption}
            isDragging={draggedIndex === index}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            setDraggedIndex={setDraggedIndex}
            setDragOverIndex={setDragOverIndex}
            handleOptionDragStart={handleOptionDragStart}
            handleOptionDragOver={handleOptionDragOver}
            handleOptionDragEnd={handleOptionDragEnd}
            draggedOptionIndex={draggedOptionIndex}
            dragOverOptionIndex={dragOverOptionIndex}
            pageOptions={questionPages}
            onMoveToPage={moveQuestionToPage}
          />
        </div>
      )); })() : <div className="text-center py-10 text-slate-500">Auf dieser Seite sind noch keine Fragen.</div>}</div>
    </div>
  );
}
